import React, { useMemo, useState, useEffect } from 'react';
import { authFetch } from '../api';

type Provider = 'openai' | 'groq' | 'xai' | 'gemini' | 'ollama';

interface SettingsResponse {
  provider: Provider;
  model: string;
  provider_default_model: string;
  providers: Provider[];
  openai_api_key_set: boolean;
  openai_api_key_masked: string;
  groq_api_key_set: boolean;
  groq_api_key_masked: string;
  xai_api_key_set: boolean;
  xai_api_key_masked: string;
  gemini_api_key_set: boolean;
  gemini_api_key_masked: string;
  ollama_api_key_set: boolean;
  ollama_api_key_masked: string;
  openai_base_url: string;
  groq_base_url: string;
  xai_base_url: string;
  gemini_base_url: string;
  ollama_base_url: string;
}

interface SettingsProps {
  open: boolean;
  onClose: () => void;
}

const PROVIDER_LABELS: Record<Provider, string> = {
  openai: 'OpenAI',
  groq: 'Groq',
  xai: 'xAI / Grok',
  gemini: 'Google Gemini',
  ollama: 'Ollama (local)',
};

const PROVIDER_DEFAULT_BASE_URLS: Record<Provider, string> = {
  openai: '',
  groq: 'https://api.groq.com/openai/v1',
  xai: 'https://api.x.ai/v1',
  gemini: 'https://generativelanguage.googleapis.com/v1beta/openai',
  ollama: 'http://localhost:11434/v1',
};

const MODEL_SUGGESTIONS: Record<Provider, string[]> = {
  openai: ['gpt-4o-mini', 'gpt-4o'],
  groq: ['llama-3.1-8b-instant', 'llama-3.3-70b-versatile'],
  xai: ['grok-2-latest'],
  gemini: ['gemini-2.0-flash', 'gemini-1.5-pro'],
  ollama: ['llama3.1:8b', 'qwen2.5:7b', 'mistral:7b'],
};

export default function Settings({ open, onClose }: SettingsProps) {
  const [provider, setProvider] = useState<Provider>('openai');
  const [model, setModel] = useState('gpt-4o-mini');
  const [providerDefaultModel, setProviderDefaultModel] = useState('gpt-4o-mini');

  const [keyInputs, setKeyInputs] = useState<Record<Provider, string>>({
    openai: '',
    groq: '',
    xai: '',
    gemini: '',
    ollama: '',
  });
  const [clearActiveKey, setClearActiveKey] = useState(false);

  const [baseUrls, setBaseUrls] = useState<Record<Provider, string>>({
    openai: '',
    groq: PROVIDER_DEFAULT_BASE_URLS.groq,
    xai: PROVIDER_DEFAULT_BASE_URLS.xai,
    gemini: PROVIDER_DEFAULT_BASE_URLS.gemini,
    ollama: PROVIDER_DEFAULT_BASE_URLS.ollama,
  });

  const [keyMeta, setKeyMeta] = useState<Record<Provider, { set: boolean; masked: string }>>({
    openai: { set: false, masked: '' },
    groq: { set: false, masked: '' },
    xai: { set: false, masked: '' },
    gemini: { set: false, masked: '' },
    ollama: { set: false, masked: '' },
  });

  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  const loadSettings = async () => {
    try {
      const res = await authFetch('/api/settings');
      if (!res.ok) throw new Error(`Failed to load settings (${res.status})`);
      const data: SettingsResponse = await res.json();
      setProvider(data.provider || 'openai');
      setModel(data.model || 'gpt-4o-mini');
      setProviderDefaultModel(data.provider_default_model || 'gpt-4o-mini');

      setBaseUrls({
        openai: data.openai_base_url || '',
        groq: data.groq_base_url || PROVIDER_DEFAULT_BASE_URLS.groq,
        xai: data.xai_base_url || PROVIDER_DEFAULT_BASE_URLS.xai,
        gemini: data.gemini_base_url || PROVIDER_DEFAULT_BASE_URLS.gemini,
        ollama: data.ollama_base_url || PROVIDER_DEFAULT_BASE_URLS.ollama,
      });

      setKeyMeta({
        openai: { set: data.openai_api_key_set, masked: data.openai_api_key_masked || '' },
        groq: { set: data.groq_api_key_set, masked: data.groq_api_key_masked || '' },
        xai: { set: data.xai_api_key_set, masked: data.xai_api_key_masked || '' },
        gemini: { set: data.gemini_api_key_set, masked: data.gemini_api_key_masked || '' },
        ollama: { set: data.ollama_api_key_set, masked: data.ollama_api_key_masked || '' },
      });

      setKeyInputs({ openai: '', groq: '', xai: '', gemini: '', ollama: '' });
      setClearActiveKey(false);
      setStatus('');
    } catch {
      setStatus('Failed to load settings');
    }
  };

  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open]);

  const activeKeyField = useMemo(() => `${provider}_api_key` as const, [provider]);
  const activeBaseUrlField = useMemo(() => `${provider}_base_url` as const, [provider]);
  const activeMaskedKey = keyMeta[provider]?.masked || '';
  const activeKeySet = !!keyMeta[provider]?.set;

  const handleSave = async () => {
    setSaving(true);
    setStatus('');

    try {
      const body: Record<string, string> = {
        provider,
        model: model.trim(),
      };

      body[activeBaseUrlField] = (baseUrls[provider] || '').trim();

      const typedKey = (keyInputs[provider] || '').trim();
      if (clearActiveKey) {
        body[activeKeyField] = '';
      } else if (typedKey) {
        body[activeKeyField] = typedKey;
      }

      const res = await authFetch('/api/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        throw new Error(`Failed to save (${res.status})`);
      }

      setStatus('Settings saved');
      await loadSettings();
    } catch (err: any) {
      setStatus(err?.message || 'Connection error');
    }

    setSaving(false);
  };

  if (!open) return null;

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div
        className="settings-modal"
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
      >
        <div className="settings-header">
          <h2>Settings</h2>
          <button className="close-btn" type="button" onClick={onClose}>
            x
          </button>
        </div>

        <div className="settings-body">
          <div className="settings-section">
            <label>Provider</label>
            <select
              value={provider}
              onChange={e => {
                const nextProvider = e.target.value as Provider;
                const nextDefaultModel = MODEL_SUGGESTIONS[nextProvider][0] || model;
                setProvider(nextProvider);
                setProviderDefaultModel(nextDefaultModel);
                // Keep provider switches runnable by default.
                // When provider changes, snap model to that provider's default.
                setModel(nextDefaultModel);
                setClearActiveKey(false);
              }}
            >
              {Object.entries(PROVIDER_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="settings-section">
            <label>Model</label>
            <input
              list={`model-suggestions-${provider}`}
              value={model}
              onChange={e => setModel(e.target.value)}
              placeholder={providerDefaultModel || MODEL_SUGGESTIONS[provider][0]}
            />
            <datalist id={`model-suggestions-${provider}`}>
              {MODEL_SUGGESTIONS[provider].map(m => (
                <option key={m} value={m} />
              ))}
            </datalist>
            <p className="settings-hint">Use any model name supported by the selected provider.</p>
          </div>

          <div className="settings-section">
            <label>{PROVIDER_LABELS[provider]} API Key</label>
            {activeKeySet && !clearActiveKey && (
              <div className="settings-current-key">
                Current: <code>{activeMaskedKey}</code>
              </div>
            )}
            <input
              type="password"
              value={keyInputs[provider]}
              onChange={e => {
                setKeyInputs(prev => ({ ...prev, [provider]: e.target.value }));
                if (clearActiveKey) setClearActiveKey(false);
              }}
              placeholder={provider === 'ollama' ? 'Optional (defaults to "ollama")' : 'Enter API key'}
              disabled={clearActiveKey}
            />
            {activeKeySet && (
              <label style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={clearActiveKey}
                  onChange={e => setClearActiveKey(e.target.checked)}
                />
                Clear saved key for this provider
              </label>
            )}
          </div>

          <div className="settings-section">
            <label>{PROVIDER_LABELS[provider]} Base URL</label>
            <input
              value={baseUrls[provider] || ''}
              onChange={e => setBaseUrls(prev => ({ ...prev, [provider]: e.target.value }))}
              placeholder={PROVIDER_DEFAULT_BASE_URLS[provider]}
            />
            <p className="settings-hint">Leave empty to use default endpoint for this provider.</p>
          </div>

          {status && (
            <div
              className={`settings-status ${
                status.toLowerCase().includes('fail') || status.toLowerCase().includes('error')
                  ? 'error'
                  : ''
              }`}
            >
              {status}
            </div>
          )}

          <button className="btn-primary" type="button" onClick={handleSave} disabled={saving || !model.trim()}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
